"use client";

import type { ReactNode } from "react";

/**
 * An event's at-a-glance details: when it is, what kind of event it is, how far
 * it walks, how many it takes and from what age, as a row of chips; whether it
 * is nearly full, full, cancelled, over or not yet taking registrations as
 * badges; its prices, with the way to the calendar; and who leads it.
 *
 * An event that has ended shows no prices, whatever the options say: there is
 * nothing left to pay for.
 */
import { HikingIcon } from "@/components/ui/HikingIcon";
import { CakeSlice, CalendarDays, CalendarPlus, Clock, Footprints, Route, Users } from "lucide-react";
import { eventDateText, euros, shortDate, timeText } from "@/lib/format";
import { cdnUrl } from "@/lib/cdn";
import type { EventWithState, Leader } from "@/lib/types/event";
import { REGISTRATION_LABELS, eventEnded, eventTypeLabel, listWords } from "@/components/event/labels";
import { openLeader } from "@/components/event/LeaderPopup";
import { addEventToCalendar } from "@/components/event/share";
import { RatingChip } from "@/components/event/EventTestimonials";
import type { Testimonials } from "@/lib/types/testimonial";

function Chip({ icon: Icon, children, tone = "plain" }: { icon?: typeof Route; children: ReactNode; tone?: "plain" | "brand" | "amber" | "danger" | "mute" }) {
  const tones = {
    plain: "bg-ground text-ink",
    brand: "bg-brand-soft text-brand-dark",
    amber: "bg-amber-soft text-[#7a5a2c]",
    danger: "bg-[hsl(4,75%,95%)] text-danger",
    mute: "bg-ground text-ink-soft",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12.5px] font-semibold ${tones[tone]}`}>
      {Icon && <Icon size={14} className={tone === "plain" ? "text-brand" : ""} />}
      {children}
    </span>
  );
}

/**
 * Whether an event still taking registrations is nearly full or full, as a
 * badge. Nothing for an event with places, with no maximum, or for a client
 * already going, who has their place.
 */
export function AvailabilityBadge({ event }: { event: Pick<EventWithState, "availability" | "places_left" | "is_registered"> }) {
  if (event.is_registered) return null;
  if (event.availability === "LAST_PLACES") return <Chip tone="amber">{event.places_left === 1 ? "Último lugar" : "Últimas vagas"}</Chip>;
  if (event.availability === "SOLD_OUT") return <Chip tone="danger">Esgotado</Chip>;
  return null;
}

/**
 * The prices an event charges today, a row per type, and while an early price
 * is still valid the later one it goes up to, crossed out before it, with the
 * last day of the early prices under them.
 */
export function PriceList({ event, className = "" }: { event: EventWithState; className?: string }) {
  if (event.price == null) {
    return null;
  }
  const rows: [string, number, number | null][] = [["Adulto", Number(event.price), event.late_price]];
  if (Number(event.price) > Number(event.price_member)) {
    rows.push(["Membro", Number(event.price_member), event.late_price_member]);
  }
  if (Number(event.price_child) > 0) {
    rows.push(["Criança", Number(event.price_child), event.late_price_child]);
  }
  return (
    <div className={className}>
      <dl className="flex flex-col gap-1">
        {rows.map(([name, price, late]) => (
          <div key={name} className="flex items-baseline justify-between gap-3 text-[14px]">
            <dt className="text-ink-soft">{name}</dt>
            <dd className="flex items-baseline gap-2 font-bold text-price">
              {late != null && Number(late) > price && <s className="text-[12.5px] font-semibold text-ink-mute">{euros(late)}</s>}
              {price > 0 ? euros(price) : "Gratuito"}
            </dd>
          </div>
        ))}
      </dl>
      {event.price_valid_until && (
        <p className="mt-1.5 inline-flex items-center gap-1.5 text-[12.5px] text-ink-soft">
          <Clock size={13} /> Preço válido até {shortDate(event.price_valid_until)}
        </p>
      )}
    </div>
  );
}

/**
 * Who leads it, as a note: for each leader, in the order staff picked them, a
 * photo small enough to read as part of the sentence, and the name. Together
 * they open that leader's profile, but only for a leader with a photo, whose
 * profile has something to show. An event nobody has been picked to lead yet
 * says so while it is still to come; one that has happened says nothing.
 * Líder, never guia.
 */
export function LeadersNote({ leaders, ended, className = "" }: { leaders: Leader[] | null | undefined; ended: boolean; className?: string }) {
  const list = leaders || [];
  if (!list.length) {
    return ended ? null : <p className={`text-[13.5px] text-ink-soft ${className}`}>Evento liderado por: por definir</p>;
  }
  const names = list.map((leader, i) => {
    const inner = (
      <>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={cdnUrl(leader.photo || "user.png")} alt="" className="h-6 w-6 rounded-full object-cover ring-1 ring-line" width={24} height={24} />
        <span className="font-semibold text-ink">{leader.name}</span>
      </>
    );
    return leader.photo ? (
      <button key={leader.id} type="button" onClick={() => openLeader(leader.id)} className="pressable inline-flex items-center gap-1.5 align-middle underline-offset-2 hover:underline">
        {inner}
      </button>
    ) : (
      <span key={`${leader.id}-${i}`} className="inline-flex items-center gap-1.5 align-middle">
        {inner}
      </span>
    );
  });
  return (
    <p className={`flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13.5px] text-ink-soft ${className}`}>
      <span>Evento liderado por</span>
      {names.map((name, i) => (
        <span key={i} className="inline-flex items-center gap-1.5">
          {i > 0 && i === names.length - 1 && <span>e</span>}
          {name}
          {i < names.length - 2 && ","}
        </span>
      ))}
    </p>
  );
}

export function EventFacts({
  event,
  totalKm = null,
  rating = null,
  prices = true,
  className = "",
}: {
  event: EventWithState;
  /** the km of the trails the programme walks, once it is known */
  totalKm?: number | null;
  /** what clients made of the programme's past events (montanhas-web#19): a chip from three ratings up */
  rating?: Testimonials | null;
  prices?: boolean;
  className?: string;
}) {
  const ended = eventEnded(event);
  const start = timeText(event.start_time);
  const type = eventTypeLabel(event.type);
  const participants = Number(event.min_participants) > 0 && Number(event.max_participants) > 0 ? `De ${event.min_participants} a ${event.max_participants} participantes` : "";
  const registration = !ended && event.state !== "CANCELED" && event.registration_state !== "OPEN" ? REGISTRATION_LABELS[event.registration_state] : "";

  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <Chip icon={CalendarDays}>{eventDateText(event.start_time, event.end_time, false)}</Chip>
        {start && start !== "00:00" && <Chip icon={Clock}>{start}</Chip>}
        {type && <Chip icon={event.type === "HIKE" ? HikingIcon : Footprints}>{type}</Chip>}
        {totalKm !== null && totalKm > 0 && <Chip icon={Route}>{`${String(Math.round(totalKm * 10) / 10).replace(".", ",")} km`}</Chip>}
        {participants && <Chip icon={Users}>{participants}</Chip>}
        {Number(event.min_age) > 0 && <Chip icon={CakeSlice}>A partir dos {event.min_age} anos</Chip>}
        {event.state === "CANCELED" ? <Chip tone="danger">Cancelado</Chip> : ended ? <Chip tone="mute">Já decorreu</Chip> : null}
        {registration && <Chip tone="mute">{registration}</Chip>}
        <AvailabilityBadge event={event} />
        <RatingChip of={rating} hasWords={!!rating?.testimonials?.length} />
      </div>
      {prices && !ended && event.state !== "CANCELED" && (
        <div className="rounded-[12px] border border-line px-4 py-3">
          <PriceList event={event} />
          <button type="button" onClick={() => addEventToCalendar(event)} className="pressable mt-2.5 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-brand">
            <CalendarPlus size={14} /> Adicionar ao calendário
          </button>
        </div>
      )}
      <LeadersNote leaders={event.leaders} ended={ended} />
    </div>
  );
}

/** "A e B" from an event's leaders, for anyone that needs the names alone */
export const leaderNames = (leaders: Leader[] | null | undefined) => listWords((leaders || []).map((leader) => leader.name).filter(Boolean));
